import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import {
  BrandLogo,
  EditItemSheet,
  PrimaryCTA,
  ScanOverlay,
} from '../../components';
import { fpColors } from '../../styles/theme';
import { clarityHelpers } from '../../utils/clarity';
import { streamScan, ScanHttpError } from '../../api/streamScan';
import { api } from '../../api/remembrApi';
import type { InventoryItemResponse } from '../../api/types';
import { useAppDispatch } from '../../store/store';
import { upsertInventoryItem } from '../browse/browseSlice';

interface SelectedImage {
  uri: string;
  name: string;
  type: string;
  size?: number;
}

/**
 * `scanning` = upload is in flight + waiting for the first item.
 * `streaming` = at least one item has arrived; more are still coming.
 * `complete` = final `complete` event received (or partial-failure
 *              absorbed with items kept).
 * `failed` = fatal error before any items were persisted.
 */
type CaptureStatus = 'idle' | 'scanning' | 'streaming' | 'complete' | 'failed';

export const UploadScreen: React.FC = () => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const { width } = useWindowDimensions();
  // Match the Dashboard breakpoint so the entry-point bypass and the
  // drop-zone idle view flip in lockstep.
  const isWebDesktop = Platform.OS === 'web' && width >= 768;

  const [selected, setSelected] = useState<SelectedImage | null>(null);
  // FE-262 — QuickAddSheet entry point on this screen was the
  // misleading "Type details instead" link. Removed; QuickAddSheet
  // stays reachable from the Dashboard chooser tile.
  const [status, setStatus] = useState<CaptureStatus>('idle');
  const [detectedItems, setDetectedItems] = useState<InventoryItemResponse[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [verifyingId, setVerifyingId] = useState<number | null>(null);
  const [roomContext, setRoomContext] = useState<string | null>(null);
  const [aiDescription, setAiDescription] = useState<string | null>(null);

  // Controller for the in-flight stream. Cancelled when the user
  // taps close or starts a new scan — items already received remain
  // persisted server-side, per the backend contract.
  const abortRef = useRef<AbortController | null>(null);

  // Cancel the stream if the component unmounts mid-scan.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  /* ————— Handlers ————— */
  const reset = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSelected(null);
    setStatus('idle');
    setDetectedItems([]);
    setErrorMessage(null);
    setRoomContext(null);
    setAiDescription(null);
  };

  const runScan = async (image: SelectedImage) => {
    // Fresh stream — abort any previous one (defensive; shouldn't
    // happen given the idle→scanning gate but guards strict-mode
    // double-invoke in dev).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('scanning');
    setDetectedItems([]);
    setErrorMessage(null);
    setRoomContext(null);
    setAiDescription(null);

    let fileRecordId: number | null = null;
    let skippedCount = 0;
    let firstItemTracked = false;

    try {
      const iter = streamScan(
        { uri: image.uri, name: image.name, type: image.type },
        { signal: controller.signal },
      );
      for await (const evt of iter) {
        switch (evt.type) {
          case 'status': {
            fileRecordId = evt.data.file_record_id;
            // eslint-disable-next-line no-console
            console.log('🔎 Upload stream: status', evt.data);
            break;
          }
          case 'item': {
            const item = evt.data;
            setDetectedItems((prev) => {
              // Prepend so the newest/most-recent lands at the top,
              // mirroring the Items grid's prepend-on-upsert.
              return [item, ...prev];
            });
            dispatch(upsertInventoryItem(item));
            setStatus('streaming');
            if (!firstItemTracked) {
              firstItemTracked = true;
              clarityHelpers.trackFeature('upload', 'ai_detection_first_item', {
                confidence: String(item.confidence_score ?? 0),
              });
            }
            break;
          }
          case 'item_error': {
            skippedCount += 1;
            // eslint-disable-next-line no-console
            console.warn('🔎 Upload stream: item_error', evt.data);
            break;
          }
          case 'summary': {
            setAiDescription(evt.data.ai_description || null);
            setRoomContext(evt.data.room_context || null);
            break;
          }
          case 'complete': {
            setStatus('complete');
            clarityHelpers.trackFeature(
              'upload',
              'ai_detection_stream_complete',
              {
                items: String(evt.data.items_count ?? 0),
                skipped: String(skippedCount),
                ms: String(evt.data.processing_time_ms ?? 0),
              },
            );
            if (skippedCount > 0) {
              Toast.show({
                type: 'info',
                text1: `${skippedCount} ${skippedCount === 1 ? 'item was' : 'items were'} skipped during analysis`,
              });
            }
            break;
          }
          case 'error': {
            const persisted = evt.data.items_persisted ?? 0;
            if (persisted > 0) {
              // Partial failure — keep what we showed, flip to
              // `complete` so the user can confirm/edit what made it.
              setStatus('complete');
              Toast.show({
                type: 'info',
                text1: `Saved ${persisted} ${persisted === 1 ? 'item' : 'items'} — analysis stopped partway`,
              });
            } else {
              setStatus('failed');
              setErrorMessage(
                evt.data.error || 'Scan failed before any items were saved.',
              );
              Toast.show({
                type: 'error',
                text1: 'Scan failed',
                text2: evt.data.error || 'Please try again.',
              });
            }
            clarityHelpers.trackError(
              evt.data.error || 'stream_error',
              'upload_stream',
            );
            break;
          }
        }
      }
    } catch (err: any) {
      // Abort from reset() / unmount — silent, items already
      // rendered stay visible. `err.name === 'AbortError'` on web,
      // `DOMException` on some engines.
      if (controller.signal.aborted) {
        // eslint-disable-next-line no-console
        console.log('🔎 Upload stream: aborted');
        return;
      }

      // eslint-disable-next-line no-console
      console.error('🔎 Upload stream failed', err);

      let message: string;
      if (err instanceof ScanHttpError) {
        if (err.status === 401) {
          message = 'Session expired — please log in again.';
        } else if (err.status === 413) {
          message = 'That file is too large. Try a smaller photo.';
        } else if (err.status === 400) {
          message = err.detail || 'We couldn\u2019t read that file.';
        } else {
          message = err.detail || `Upload failed (HTTP ${err.status}).`;
        }
      } else {
        message = 'Check your connection and try again.';
      }

      // If we got some items before the exception, keep them rendered.
      setDetectedItems((prev) => {
        if (prev.length > 0) {
          setStatus('complete');
          Toast.show({
            type: 'info',
            text1: `Saved ${prev.length} ${prev.length === 1 ? 'item' : 'items'} — analysis stopped partway`,
            text2: message,
          });
        } else {
          setErrorMessage(message);
          setStatus('failed');
          Toast.show({ type: 'error', text1: 'Scan failed', text2: message });
        }
        return prev;
      });

      clarityHelpers.trackError(
        err?.message || 'Stream scan failed',
        'upload_stream',
      );
    } finally {
      // File-record hookup point for a future "View file" link on the
      // success card. Keep the log for telemetry correlation.
      if (fileRecordId != null) {
        // eslint-disable-next-line no-console
        console.log('🔎 Upload stream: finished', { fileRecordId });
      }
    }
  };

  const ensureMediaPermission = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission needed', 'We need access to your photos to add items.');
      return false;
    }
    return true;
  };

  const ensureCameraPermission = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission needed', 'We need camera access to take a photo.');
      return false;
    }
    return true;
  };

  const selectFromLibrary = async () => {
    clarityHelpers.trackInteraction('upload_pick_library', 'click');
    if (!(await ensureMediaPermission())) return;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: false,
      quality: 0.85,
    });
    if (result.canceled || !result.assets?.[0]) return;
    handleChosen(result.assets[0]);
  };

  const takePhoto = async () => {
    clarityHelpers.trackInteraction('upload_take_photo', 'click');
    if (!(await ensureCameraPermission())) return;
    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: false,
      quality: 0.85,
    });
    if (result.canceled || !result.assets?.[0]) return;
    handleChosen(result.assets[0]);
  };

  const handleChosen = (asset: ImagePicker.ImagePickerAsset) => {
    const image: SelectedImage = {
      uri: asset.uri,
      name: asset.fileName || `image_${Date.now()}.jpg`,
      type: (asset as any).mimeType || 'image/jpeg',
      size: asset.fileSize,
    };
    setSelected(image);
    // Kick off the upload so the scanner animation has something real to
    // track. The upload hits /upload/ (uploadContent) which returns the
    // extracted `inventory_items` in the response — no secondary fetch.
    runScan(image);
  };

  // Drop / paste / browse on web desktop hands us raw File objects.
  // Multi-file batches are out of scope for v1 — we scan the first
  // and surface a toast so the user knows the rest were ignored.
  const handleWebFiles = (files: File[]) => {
    if (files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      Toast.show({
        type: 'error',
        text1: 'Only image files are supported',
        text2: file.type || 'unknown type',
      });
      return;
    }
    if (files.length > 1) {
      Toast.show({
        type: 'info',
        text1: `Scanning the first photo`,
        text2: `${files.length - 1} more queued — multi-scan coming soon.`,
      });
    }
    const image: SelectedImage = {
      uri: URL.createObjectURL(file),
      name: file.name || `image_${Date.now()}.jpg`,
      type: file.type || 'image/jpeg',
      size: file.size,
    };
    clarityHelpers.trackInteraction('upload_web_drop', 'drop');
    setSelected(image);
    runScan(image);
  };

  const retakePhoto = () => {
    reset();
  };

  const confirmAll = () => {
    // Items are already persisted server-side by the time they
    // stream in. Confirm All just resets the local UI so the user
    // can scan another image.
    clarityHelpers.trackFeature('upload', 'confirm_all', {
      items: String(detectedItems.length),
    });
    reset();
  };

  /**
   * Flip `is_verified` to true for a single row. Mirrors the Confirm
   * path in VerifyScreen: PATCH /users/inventory/{id} with
   * `{ is_verified: true }`, then update the local detected list
   * AND `dispatch(upsertInventoryItem(...))` so the Items grid / Verify
   * queue reflect the change immediately. On backend failure we
   * optimistically flip the row client-side and surface a toast —
   * the server still has the item persisted as unverified, so a
   * refetch will resync if the patch never landed.
   */
  const handleConfirmRow = async (index: number) => {
    const item = detectedItems[index];
    if (!item?.id) return;
    setVerifyingId(item.id);
    try {
      let updated: InventoryItemResponse;
      try {
        updated = await api.updateInventoryItem(item.id, {
          is_verified: true,
        });
      } catch {
        updated = { ...item, is_verified: true };
        Toast.show({
          type: 'info',
          text1: 'Marked verified locally',
          text2: 'We\u2019ll retry when you reopen this item.',
        });
      }
      setDetectedItems((prev) => {
        const next = prev.slice();
        next[index] = updated;
        return next;
      });
      dispatch(upsertInventoryItem(updated));
      clarityHelpers.trackFeature('upload', 'confirm_row', {
        id: String(item.id),
      });
    } finally {
      setVerifyingId(null);
    }
  };

  return (
    <View style={styles.page}>
      {/* Custom top bar — matches Dashboard */}
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <BrandLogo variant="horizontal" height={24} tone="color" />
          <Pressable
            onPress={() => (navigation as any).goBack()}
            style={styles.closeBtn}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Close"
          >
            <MaterialIcons name="close" size={22} color={fpColors.onSurfaceVariant} />
          </Pressable>
        </View>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {status === 'idle' ? (
          isWebDesktop ? (
            <WebDesktopDropIdleView onFiles={handleWebFiles} />
          ) : (
            <IdleView onTake={takePhoto} onPick={selectFromLibrary} />
          )
        ) : (
          <ScanView
            image={selected!}
            status={status}
            detectedItems={detectedItems}
            errorMessage={errorMessage}
            roomContext={roomContext}
            aiDescription={aiDescription}
            verifyingId={verifyingId}
            onRetake={retakePhoto}
            onRetry={() => selected && runScan(selected)}
            onConfirmAll={confirmAll}
            onEditRow={(idx) => setEditingIndex(idx)}
            onConfirmRow={handleConfirmRow}
          />
        )}
      </ScrollView>

      <EditItemSheet
        visible={editingIndex !== null}
        item={
          editingIndex !== null
            ? (detectedItems[editingIndex] ?? null)
            : null
        }
        onClose={() => setEditingIndex(null)}
        onSaved={(updated) => {
          if (editingIndex === null) return;
          setDetectedItems((prev) => {
            const next = prev.slice();
            next[editingIndex] = updated;
            return next;
          });
          dispatch(upsertInventoryItem(updated));
        }}
      />

      {/* FE-262 — QuickAddSheet mount removed alongside the misleading
          "Type details" link. Dashboard chooser tile still owns the
          Quick add entry point. */}
    </View>
  );
};

/* ————— Idle state: pick or take a photo ————— */

const IdleView: React.FC<{ onTake: () => void; onPick: () => void }> = ({
  onTake,
  onPick,
}) => (
  <View style={styles.idle}>
    <View style={styles.idleHero}>
      <View style={styles.idleIconBadge}>
        <MaterialIcons name="qr-code-scanner" size={32} color={fpColors.primary} />
      </View>
      <Text style={styles.idleTitle}>Add a new item</Text>
      <Text style={styles.idleSubtitle}>
        Take a photo or pick from your library. Our AI identifies the items for
        you and adds them to your inventory.
      </Text>
    </View>

    <View style={styles.idleActions}>
      <Pressable
        onPress={onTake}
        style={({ pressed }) => [
          styles.idleAction,
          styles.idleActionPrimary,
          pressed && styles.idleActionPressed,
        ]}
      >
        <MaterialIcons name="photo-camera" size={26} color={fpColors.onPrimary} />
        <Text style={styles.idleActionLabelPrimary}>Take Photo</Text>
        <Text style={styles.idleActionMetaPrimary}>Snap & scan</Text>
      </Pressable>

      <Pressable
        onPress={onPick}
        style={({ pressed }) => [
          styles.idleAction,
          styles.idleActionSecondary,
          pressed && styles.idleActionPressed,
        ]}
      >
        <MaterialIcons name="photo-library" size={26} color={fpColors.primary} />
        <Text style={styles.idleActionLabelSecondary}>From Library</Text>
        <Text style={styles.idleActionMetaSecondary}>Pick an image</Text>
      </Pressable>
    </View>

    <View style={styles.idleHint}>
      <MaterialIcons name="auto-awesome" size={14} color={fpColors.primary} />
      <Text style={styles.idleHintText}>
        Tip: a well-lit, straight-on shot helps the AI recognize more items.
      </Text>
    </View>
  </View>
);

/* ————— Web-desktop idle: drag-drop hero + paste + browse ————— */

interface WebDesktopDropIdleViewProps {
  onFiles: (files: File[]) => void;
}

const WebDesktopDropIdleView: React.FC<WebDesktopDropIdleViewProps> = ({
  onFiles,
}) => {
  const [dragging, setDragging] = useState(false);
  const dropRef = useRef<View>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Lazily create a hidden <input type="file"> the first time the
  // user clicks Browse; reuses it on subsequent clicks. Mirrors the
  // pattern in QuickAddSheet so we don't double-register listeners.
  const openBrowse = () => {
    if (!fileInputRef.current) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.style.display = 'none';
      input.addEventListener('change', () => {
        const files = Array.from(input.files ?? []);
        // Reset so picking the same file twice still fires `change`.
        input.value = '';
        if (files.length) onFiles(files);
      });
      document.body.appendChild(input);
      fileInputRef.current = input;
    }
    fileInputRef.current.click();
  };

  // Drop handlers — attached to the underlying DOM node via ref so we
  // get the native dataTransfer API (RN's onDrop synthetic event does
  // not expose files reliably across browsers).
  useEffect(() => {
    const node = dropRef.current as unknown as HTMLElement | null;
    if (!node) return;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      // Only un-highlight when the drag leaves the zone itself, not
      // a child — relatedTarget is null when leaving the window.
      if (!node.contains(e.relatedTarget as Node | null)) {
        setDragging(false);
      }
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) onFiles(files);
    };

    node.addEventListener('dragover', onDragOver);
    node.addEventListener('dragleave', onDragLeave);
    node.addEventListener('drop', onDrop);
    return () => {
      node.removeEventListener('dragover', onDragOver);
      node.removeEventListener('dragleave', onDragLeave);
      node.removeEventListener('drop', onDrop);
    };
  }, [onFiles]);

  // Paste from clipboard (⌘V / Ctrl+V) — window-level so it works
  // even when the drop zone isn't focused. Filters to image MIME so
  // pasting plain text doesn't fire a bogus upload.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const files = items
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f != null);
      if (files.length) onFiles(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onFiles]);

  // Prevent the browser's default "open file" behavior if the user
  // drops outside the zone — without this, a stray drop navigates
  // away from the app.
  useEffect(() => {
    const swallow = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  return (
    <View style={styles.dropWrap}>
      <View style={styles.dropHero}>
        <Text style={styles.dropTitle}>Add items</Text>
        <Text style={styles.dropSubtitle}>
          Drop photos here, click to browse, or paste from your clipboard.
          Our AI identifies each item and adds it to your inventory.
        </Text>
      </View>

      <Pressable
        // @ts-ignore — RN Web passes refs through to the underlying div.
        ref={dropRef}
        onPress={openBrowse}
        accessibilityRole="button"
        accessibilityLabel="Drop photos or click to browse"
        style={[styles.dropZone, dragging && styles.dropZoneActive]}
        testID="upload-drop-zone"
      >
        <View style={styles.dropIconBubble}>
          <MaterialIcons
            name={dragging ? 'file-download' : 'cloud-upload'}
            size={40}
            color={fpColors.primary}
          />
        </View>
        <Text style={styles.dropZoneTitle}>
          {dragging ? 'Drop to scan' : 'Drop photos here'}
        </Text>
        <Text style={styles.dropZoneSubtitle}>
          or <Text style={styles.dropZoneLink}>click to browse</Text>
        </Text>
        <View style={styles.dropZoneKbdRow}>
          <View style={styles.kbd}>
            <Text style={styles.kbdText}>⌘V</Text>
          </View>
          <Text style={styles.dropZoneKbdHint}>paste a screenshot</Text>
        </View>
      </Pressable>

      <View style={styles.dropHint}>
        <MaterialIcons name="auto-awesome" size={14} color={fpColors.primary} />
        <Text style={styles.idleHintText}>
          Tip: a well-lit, straight-on shot helps the AI recognize more items.
        </Text>
      </View>

      {/* FE-262 — "No photo? Type item details instead" link removed.
          The link routed to QuickAddSheet which still requires a
          photo (file_record_id at the BE level), so the affordance
          was dishonest. */}
    </View>
  );
};

/* ————— Scan state: image + overlay + items list ————— */

interface ScanViewProps {
  image: SelectedImage;
  status: CaptureStatus;
  detectedItems: InventoryItemResponse[];
  errorMessage: string | null;
  roomContext: string | null;
  aiDescription: string | null;
  verifyingId: number | null;
  onRetake: () => void;
  onRetry: () => void;
  onConfirmAll: () => void;
  onEditRow: (index: number) => void;
  onConfirmRow: (index: number) => void;
}

const ScanView: React.FC<ScanViewProps> = ({
  image,
  status,
  detectedItems,
  errorMessage,
  roomContext,
  aiDescription,
  verifyingId,
  onRetake,
  onRetry,
  onConfirmAll,
  onEditRow,
  onConfirmRow,
}) => {
  const imageAspect = 4 / 3;
  const [imageHeight, setImageHeight] = useState(240);

  const onImageLayout = (e: any) => {
    const w = e?.nativeEvent?.layout?.width ?? 0;
    if (w > 0) setImageHeight(Math.round(w / imageAspect));
  };

  const scanning = status === 'scanning';
  const streaming = status === 'streaming';
  const complete = status === 'complete';
  const failed = status === 'failed';
  const busy = scanning || streaming;
  const itemCount = detectedItems.length;

  return (
    <View style={styles.scan}>
      {/* Image with scanning overlay */}
      <View
        style={[styles.imageWrap, { aspectRatio: imageAspect }]}
        onLayout={onImageLayout}
      >
        <Image source={{ uri: image.uri }} style={styles.imageFill} />
        {!failed && <ScanOverlay height={imageHeight} active={busy} />}
        {failed && (
          <View style={styles.failedOverlay}>
            <MaterialIcons name="error-outline" size={28} color={fpColors.error} />
            <Text style={styles.failedTitle}>Scan failed</Text>
            <Text style={styles.failedMeta}>
              {errorMessage || 'Something went wrong.'}
            </Text>
          </View>
        )}
      </View>

      {/* Status strip under the image */}
      <View style={styles.statusStrip}>
        {scanning && (
          <>
            <MaterialIcons
              name="auto-awesome"
              size={16}
              color={fpColors.primary}
            />
            <Text style={styles.statusStripText}>
              Analyzing with AI\u2026
            </Text>
          </>
        )}
        {streaming && (
          <>
            <MaterialIcons
              name="auto-awesome"
              size={16}
              color={fpColors.primary}
            />
            <Text style={styles.statusStripText}>
              Found {itemCount} so far\u2026 still scanning
            </Text>
          </>
        )}
        {complete && (
          <>
            <MaterialIcons name="check-circle" size={16} color="#22C55E" />
            <Text style={[styles.statusStripText, { color: '#22C55E' }]}>
              {itemCount > 0
                ? `${itemCount} item${itemCount === 1 ? '' : 's'} detected`
                : 'No items detected — try a clearer shot.'}
            </Text>
          </>
        )}
        {failed && (
          <>
            <MaterialIcons
              name="error-outline"
              size={16}
              color={fpColors.error}
            />
            <Text
              style={[styles.statusStripText, { color: fpColors.error }]}
            >
              Scan failed
            </Text>
          </>
        )}
      </View>

      {/* Scene context card (room + AI description). Appears after
          the `summary` event lands. */}
      {(roomContext || aiDescription) && (complete || streaming) ? (
        <View style={styles.contextCard}>
          {roomContext ? (
            <View style={styles.contextRoomRow}>
              <MaterialIcons
                name="place"
                size={14}
                color={fpColors.primary}
              />
              <Text style={styles.contextRoom}>{roomContext}</Text>
            </View>
          ) : null}
          {aiDescription ? (
            <Text style={styles.contextDescription} numberOfLines={3}>
              {aiDescription}
            </Text>
          ) : null}
        </View>
      ) : null}

      {/* Detected Items list — shows as soon as the first item
          streams in. Trailing skeleton row stays visible while we
          wait for more. */}
      {(streaming || complete) && itemCount > 0 && (
        <View style={styles.listBlock}>
          <View style={styles.listHeader}>
            <Text style={styles.listHeaderTitle}>Detected Items</Text>
            <Text style={styles.listHeaderMeta}>
              {streaming
                ? `${itemCount} so far\u2026`
                : `${itemCount} found`}
            </Text>
          </View>
          <View style={styles.list}>
            {detectedItems.map((item, idx) => (
              <DetectedItemRow
                key={(item.id ?? idx).toString()}
                item={item}
                busy={verifyingId === item.id}
                onEdit={() => onEditRow(idx)}
                onConfirm={() => onConfirmRow(idx)}
              />
            ))}
            {streaming ? <SkeletonRow /> : null}
          </View>
        </View>
      )}

      {/* Pre-first-item loading state: just the skeleton rows. */}
      {scanning && itemCount === 0 && (
        <View style={styles.listBlock}>
          <View style={styles.listHeader}>
            <Text style={styles.listHeaderTitle}>Detected Items</Text>
            <Text style={styles.listHeaderMeta}>scanning\u2026</Text>
          </View>
          <View style={styles.list}>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </View>
        </View>
      )}

      {/* Bottom action bar */}
      <View style={styles.actionBar}>
        <Pressable
          onPress={onRetake}
          style={({ pressed }) => [
            styles.actionSecondary,
            pressed && styles.actionPressed,
          ]}
        >
          <MaterialIcons name="replay" size={18} color={fpColors.onSurface} />
          <Text style={styles.actionSecondaryLabel}>Retake</Text>
        </Pressable>
        {failed ? (
          <View style={{ flex: 2 }}>
            <PrimaryCTA label="Try again" onPress={onRetry} iconRight={null} />
          </View>
        ) : (
          <View style={{ flex: 2 }}>
            <PrimaryCTA
              label={complete ? 'Confirm All' : 'Analyzing\u2026'}
              onPress={onConfirmAll}
              iconRight={complete ? 'check-circle' : null}
              disabled={!complete}
              loading={busy}
            />
          </View>
        )}
      </View>
    </View>
  );
};

/* ————— Detected item row ————— */

/**
 * Map a backend category string (electronics, cleaning_supplies, …) to a
 * Material Icons glyph. Falls back to inventory for anything unknown.
 */
const categoryIcon = (category?: string): keyof typeof MaterialIcons.glyphMap => {
  if (!category) return 'inventory';
  const c = category.toLowerCase();
  if (c.includes('electron') || c.includes('appliance')) return 'devices';
  if (c.includes('kitchen')) return 'kitchen';
  if (c.includes('clean')) return 'cleaning-services';
  if (c.includes('furniture') || c.includes('chair') || c.includes('sofa')) return 'weekend';
  if (c.includes('food') || c.includes('grocer')) return 'restaurant';
  if (c.includes('clothing') || c.includes('apparel') || c.includes('fashion')) return 'checkroom';
  if (c.includes('book')) return 'menu-book';
  if (c.includes('tool')) return 'handyman';
  if (c.includes('art') || c.includes('decor')) return 'palette';
  if (c.includes('sport') || c.includes('fitness')) return 'fitness-center';
  if (c.includes('toy') || c.includes('game')) return 'toys';
  if (c.includes('jewel') || c.includes('watch')) return 'watch';
  if (c.includes('toilet') || c.includes('bath') || c.includes('personal')) return 'soap';
  if (c.includes('coffee') || c.includes('drink')) return 'local-cafe';
  return 'inventory';
};

const DetectedItemRow: React.FC<{
  item: InventoryItemResponse;
  busy: boolean;
  onEdit: () => void;
  onConfirm: () => void;
}> = ({ item, busy, onEdit, onConfirm }) => {
  // Single source of truth: the backend's `is_verified` field.
  // Every streamed item lands as false, so the user has to
  // explicitly confirm each row to flip it. Confidence score is
  // surfaced elsewhere (EditItemSheet, meta row) but never drives
  // the verified state.
  const isVerified = item.is_verified === true;
  // Show location if the backend populated it; otherwise fall back
  // to category so users always see at least one contextual hint.
  const contextText = item.location || item.item_category || '';
  const icon = categoryIcon(item.item_category);

  return (
    <View style={[styles.row, !isVerified && styles.rowReview]}>
      <View style={[styles.rowIcon, !isVerified && styles.rowIconReview]}>
        <MaterialIcons
          name={icon}
          size={22}
          color={isVerified ? fpColors.primary : fpColors.tertiary}
        />
      </View>
      <View style={styles.rowBody}>
        <Text style={styles.rowName} numberOfLines={1}>
          {item.item_name || 'Unnamed item'}
        </Text>
        {!!contextText && (
          <View style={styles.rowMetaRow}>
            <MaterialIcons
              name={item.location ? 'location-on' : 'label'}
              size={12}
              color={fpColors.outline}
            />
            <Text style={styles.rowMeta} numberOfLines={1}>
              {contextText}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.rowRight}>
        <View
          style={[
            styles.badge,
            isVerified ? styles.badgeVerified : styles.badgeReview,
          ]}
        >
          <Text
            style={[
              styles.badgeText,
              isVerified ? styles.badgeTextVerified : styles.badgeTextReview,
            ]}
          >
            {isVerified ? 'VERIFIED' : 'REVIEW'}
          </Text>
        </View>
        <View style={styles.rowActionRow}>
          {/* Edit is always reachable — users should be able to fix a
              bad name / value BEFORE confirming. */}
          <Pressable onPress={onEdit} hitSlop={6} disabled={busy}>
            <Text style={styles.editLinkSecondary}>Edit</Text>
          </Pressable>
          {/* Confirm only on REVIEW rows. Fires PATCH is_verified=true
              via the handler on the screen; shows a spinner until the
              PATCH resolves. */}
          {!isVerified ? (
            <>
              <Text style={styles.actionDivider}>·</Text>
              <Pressable
                onPress={onConfirm}
                hitSlop={6}
                disabled={busy}
                style={busy && { opacity: 0.6 }}
                accessibilityRole="button"
                accessibilityLabel="Confirm item"
              >
                {busy ? (
                  <ActivityIndicator
                    size="small"
                    color={fpColors.primary}
                  />
                ) : (
                  <Text style={styles.editLink}>Confirm</Text>
                )}
              </Pressable>
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
};

/* ————— Skeleton loader ————— */

const SkeletonRow: React.FC = () => (
  <View style={styles.row}>
    <View style={styles.skelIcon} />
    <View style={styles.rowBody}>
      <View style={[styles.skelLine, { width: '60%' }]} />
      <View style={[styles.skelLine, { width: '40%', marginTop: 6 }]} />
    </View>
    <View style={styles.skelBadge} />
  </View>
);

/* ————— styles ————— */

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },

  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 32 },

  /* Idle */
  idle: {
    flex: 1,
    paddingTop: 12,
  },
  idleHero: {
    alignItems: 'center',
    marginBottom: 28,
  },
  idleIconBadge: {
    width: 64,
    height: 64,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  idleTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onBackground,
    marginBottom: 6,
  },
  idleSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    maxWidth: 320,
  },
  idleActions: {
    gap: 12,
    marginBottom: 20,
  },
  idleAction: {
    flex: 1,
    borderRadius: 16,
    padding: 20,
    minHeight: 140,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  idleActionPressed: { opacity: 0.88 },
  idleActionPrimary: {
    backgroundColor: fpColors.primary,
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 6,
  },
  idleActionSecondary: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  idleActionLabelPrimary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onPrimary,
  },
  idleActionMetaPrimary: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: `${fpColors.onPrimary}c0`,
  },
  idleActionLabelSecondary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  idleActionMetaSecondary: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  idleHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}0f`,
  },
  idleHintText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },

  /* Scan state */
  scan: {
    gap: 16,
  },
  imageWrap: {
    position: 'relative',
    width: '100%',
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: fpColors.surfaceContainer,
  },
  imageFill: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  failedOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255,255,255,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    padding: 20,
  },
  failedTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.error,
  },
  failedMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    maxWidth: 280,
  },

  statusStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}0f`,
  },
  statusStripText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },

  contextCard: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 6,
  },
  contextRoomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  contextRoom: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  contextDescription: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
  },

  listBlock: { gap: 10 },
  listHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 2,
  },
  listHeaderTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  listHeaderMeta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  list: { gap: 8 },

  /* Row */
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  rowReview: {
    borderColor: `${fpColors.tertiaryContainer}99`,
    borderWidth: 2,
  },
  rowIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}10`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowIconReview: {
    backgroundColor: `${fpColors.tertiaryContainer}22`,
  },
  rowBody: { flex: 1 },
  rowName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  rowMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  rowMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  badgeVerified: { backgroundColor: '#DCFCE7' },
  badgeReview: { backgroundColor: fpColors.tertiaryContainer },
  badgeText: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 9,
    letterSpacing: 0.4,
  },
  badgeTextVerified: { color: '#166534' },
  badgeTextReview: { color: fpColors.onTertiaryContainer },
  editLink: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
    textDecorationLine: 'underline',
  },
  editLinkSecondary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textDecorationLine: 'underline',
  },
  rowActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  actionDivider: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.outline,
  },

  /* Skeleton */
  skelIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainer,
  },
  skelLine: {
    height: 10,
    borderRadius: 5,
    backgroundColor: fpColors.surfaceContainer,
  },
  skelBadge: {
    width: 60,
    height: 18,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainer,
  },

  /* Action bar */
  actionBar: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
    marginTop: 4,
  },
  actionSecondary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    minHeight: 56,
  },
  actionPressed: { opacity: 0.85 },
  actionSecondaryLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },

  /* ——— Web-desktop drop zone ——— */
  dropWrap: {
    paddingTop: 24,
    paddingBottom: 32,
    paddingHorizontal: 24,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
    gap: 20,
  },
  dropHero: {
    alignItems: 'center',
    gap: 6,
  },
  dropTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 24,
    color: fpColors.onSurface,
  },
  dropSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.outline,
    textAlign: 'center',
    maxWidth: 480,
  },
  dropZone: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: fpColors.outlineVariant,
    borderRadius: 16,
    paddingVertical: 56,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
    gap: 12,
    // @ts-ignore — RN Web only
    cursor: 'pointer',
    // @ts-ignore — RN Web only
    transition: 'border-color 120ms, background-color 120ms',
  },
  dropZoneActive: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primaryContainer,
  },
  dropIconBubble: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primaryContainer,
    marginBottom: 4,
  },
  dropZoneTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  dropZoneSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.outline,
  },
  dropZoneLink: {
    color: fpColors.primary,
    fontFamily: 'Inter_700Bold',
  },
  dropZoneKbdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  kbd: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surface,
  },
  kbdText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurface,
    letterSpacing: 0.3,
  },
  dropZoneKbdHint: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
  },
  dropHint: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
});
